<script setup>
import { computed, reactive, ref, unref, watch } from 'vue'
import { time2Date } from '@/utils/tools'
import { sub } from '@/utils/math'
import xButton from '@/components/common/xButton.vue'
import xModal from '@/components/common/xModal.vue'

const props = defineProps({
  single: {
    type: Boolean,
    default: false,
  },
})
const emits = defineEmits(['change'])
const date = defineModel({
  type: [Number, Array],
  default: () => [],
})
const show = defineModel('show', {
  type: Boolean,
  default: false,
})
const { t } = useI18n()
const xDate = ref(null)
const scrollItemHeight = ref(0)

const defaultDate = 0
const day = 24 * 60 * 60 * 1000

const startDate = ref('')
const endDate = ref('')
const curDate = ref(0) // 0 startDate 1 endDate
const curNum = ref(0) // 7 30 90

const activeDate = computed(() => {
  const start = new Date(startDate.value)
  const end = new Date(endDate.value)
  const now = new Date()

  if (time2Date(+end, 'y-m-d') === time2Date(+now, 'y-m-d')) {
    return sub(+end, +start).div(day).toNumber()
  }
  return null
})

watch(show, toogleShow, { immediate: true })
function toogleShow(bol) {
  if (bol) {
    reset()
    show.value = true
  }
  else {
    show.value = false
  }
}
const selectorRaw = reactive({
  y: 0,
  m: 0,
  d: 0,
})
const selectorIndex = reactive({
  y: 0,
  m: 0,
  d: 0,
})
const selectorActiveIndex = reactive({
  y: 0,
  m: 0,
  d: 0,
})
const selectorStartClientY = reactive({
  y: 0,
  m: 0,
  d: 0,
})
const selectorMoveY = reactive({
  y: 0,
  m: 0,
  d: 0,
})
const selectorList = reactive({
  y: Array.from({ length: 20 }).map((_, i) => new Date().getFullYear() - 20 + i + 1),
  m: Array.from({ length: 12 }).map((_, i) => i + 1),
  d: [],
})
function formatDateSelector(date) {
  const d = date ?? new Date(startDate.value)
  selectorRaw.y = d.getFullYear()
  selectorRaw.m = d.getMonth() + 1
  selectorRaw.d = d.getDate()
  setSelectorListDay()
  selectorActiveIndex.y = selectorIndex.y = selectorList.y.indexOf(selectorRaw.y)
  selectorActiveIndex.m = selectorIndex.m = selectorList.m.indexOf(selectorRaw.m)
  selectorActiveIndex.d = selectorIndex.d = selectorList.d.indexOf(selectorRaw.d)
}

function setSelectorListDay() {
  const year = selectorRaw.y
  const month = selectorRaw.m
  selectorList.d = Array.from({ length: new Date(year, month, 0).getDate() }).map((_, i) => i + 1)
  if (selectorRaw.d > selectorList.d.length) {
    selectorRaw.d = selectorList.d[selectorList.d.length - 1]
    selectorActiveIndex.d = selectorIndex.d = selectorList.d.indexOf(selectorRaw.d)
  }
}

function getSelectorItemHeight() {
  if (scrollItemHeight.value > 0) {
    return Promise.resolve(scrollItemHeight.value)
  }
  return new Promise((resolve) => {
    const element = document.querySelector('.x-date-scroll-item')
    const height = element.getBoundingClientRect().height
    scrollItemHeight.value = height
    resolve(height)
  })
}

function ok() {
  date.value = props.single ? new Date(startDate.value).getTime() : [new Date(startDate.value).getTime(), new Date(endDate.value).getTime()]
  emits('change', { startDate: new Date(startDate.value), endDate: new Date(endDate.value), date: new Date(startDate.value), dayNum: curNum.value })
  show.value = false
}

function reset() {
  curDate.value = 0
  startDate.value = props.single ? time2Date(+date.value || Date.now(), 'y-m-d') : time2Date(date.value[0] || (Date.now() - defaultDate * day), 'y-m-d')
  endDate.value = time2Date(date.value[1] || Date.now(), 'y-m-d')
  formatDateSelector(new Date(startDate.value))
  curNum.value = 0
}

const screenWidth = window.innerWidth
const calcTranslateY = computed(() => {
  return (type) => {
    return `translateY(${selectorMoveY[type] / 16 / (screenWidth / 375) - selectorIndex[type] * 2.5}rem)`
  }
})
function changeDate(num) {
  const now = Date.now()
  const start = now - num * day
  startDate.value = time2Date(+start, 'y-m-d')
  endDate.value = time2Date(+now, 'y-m-d')
  curDate.value = 0
  formatDateSelector(new Date(startDate.value))
  curNum.value = num
}
function changeChooseDate(type) {
  curDate.value = type
  formatDateSelector(new Date(type === 0 ? startDate.value : endDate.value))
}
function setChooseDate() {
  const dateStr = `${selectorRaw.y}/${selectorRaw.m || 1}/${selectorRaw.d || 1}`
  const d = time2Date(new Date(dateStr).getTime(), 'y-m-d')
  if (curDate.value === 0) {
    startDate.value = d
  }
  else {
    endDate.value = d
  }
  curNum.value = ''
}
function onTouchstart(type, e) {
  selectorStartClientY[type] = e.touches[0].clientY
}
async function onTouchend(type) {
  selectorIndex[type] = selectorActiveIndex[type]
  selectorStartClientY[type] = 0
  selectorMoveY[type] = 0
  selectorRaw[type] = selectorList[type][selectorIndex[type]]
  setSelectorListDay()
  setChooseDate()
}
async function onScroll(type, e) {
  selectorMoveY[type] = e.touches[0].clientY - selectorStartClientY[type]
  const itemHeight = await getSelectorItemHeight()
  const activeIndex = Math.round(-selectorMoveY[type] / itemHeight) + selectorIndex[type]
  const max = unref(selectorList[type]).length - 1
  selectorActiveIndex[type] = activeIndex < 0 ? 0 : activeIndex >= max ? max : activeIndex
}

function getDayText(day) {
  const textMap = {
    0: t('全部'),
    1: t('昨天'),
    7: t('近7日'),
    30: t('近30日'),
  }
  return textMap[day]
}
</script>

<template>
  <xModal
    ref="xDate"
    :custom-style="{
      position: 'fixed',
      left: '0',
      right: '0',
      bottom: '0',
      width: '100%',
      borderRadius: '16px 16px 0 0',
      transform: 'translateY(0)',
      animation: 'slide-up 0.3s ease-out',
    }"
    :show="show"
    :header-style="{ display: 'none' }"
    :ok="ok"
    @mask-click="show = false"
  >
    <div class="flex items-center justify-between p-4 border-b border-border">
      <text class="text-textTertiary" @click="reset">
        {{ $t('重置') }}
      </text>
      <text class="text-base font-medium">
        {{ $t('时间选择') }}
      </text>
      <text class="text-primary" @click="ok">
        {{ $t('确认') }}
      </text>
    </div>
    <div class="space-y-4 py-4">
      <template v-if="!single">
        <slot name="prefix" />
        <div class="grid grid-cols-3 gap-3">
          <xButton
            v-for="itemDay in [0, 1, 7]"
            :key="itemDay"
            :type="activeDate === itemDay ? 'default' : 'tertiary'"
            :class="{
              '!border-primary !text-primary': activeDate === itemDay,
              'hover:!border-primary hover:!text-primary': activeDate !== itemDay,
            }"
            class="w-full !h-9 !text-xs transition-colors duration-200"
            @click="changeDate(itemDay)"
          >
            {{ $t(getDayText(itemDay)) }}
          </xButton>
        </div>
        <div class="flex items-center justify-between space-x-4 text-xs">
          <div class="flex-1 bg-pageTertiary h-9 leading-9 px-2.5 rounded" :class="{ '!text-primary': curDate === 0 }" @click="changeChooseDate(0)">
            {{ startDate }}
          </div>
          <text>-</text>
          <div class="flex-1 bg-pageTertiary h-9 leading-9 px-2.5 rounded" :class="{ '!text-primary': curDate === 1 }" @click="changeChooseDate(1)">
            {{ endDate }}
          </div>
        </div>
      </template>
      <div class="relative grid grid-cols-3 gap-3 leading-10 text-center">
        <div class="absolute left-0 z-0 w-full h-10 top-20 bg-pageTertiary x-date-scroll-item" />
        <div
          v-for="k in ['y', 'm', 'd']"
          :key="k"
          class="relative h-[12.5rem] overflow-hidden z-10 x-date-scroll-y"
        >
          <div
            class="absolute top-0 left-0 w-full py-20 text-textTertiary"
            :style="{ transform: calcTranslateY(k) }"
            @touchmove.stop.prevent="onScroll(k, $event)"
            @touchstart.stop.prevent="onTouchstart(k, $event)"
            @touchend.stop.prevent="onTouchend(k, $event)"
          >
            <div v-for="i, index in unref(selectorList[k])" :key="`${k}-${i}`" :class="{ 'text-textPrimary': selectorActiveIndex[k] === index }" class="h-10">
              {{ i }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </xModal>
</template>
